import { pairHasOpenIssue, useTaskActivitiesForServiceRequest } from '@commenda/api/tasks';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus } from '../../../utils';
import { USCCORPIncorporationTasks } from '../config';
import { DocumentVerificationModal } from './DocumentVerificationModal';

export function VerifyDocuments({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) {
  const { role, mutate, serviceRequest } = context;

  const verificationDocModal = useToggle();

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );
  const clientUploadDocumentTask = serviceRequest?.taskGroups
    ?.find((srtg) => srtg.id === task.taskGroupId)
    ?.tasks?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        USCCORPIncorporationTasks.CLIENT_UPLOADS_SIGNED_SHAREHOLDING_DOCUMENTS
    );
  const taskHasOpenIssue = pairHasOpenIssue(
    activityFeed?.activities,
    clientUploadDocumentTask,
    task
  );

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) =>
      ServiceRequestTaskIconConfig[taskHasOpenIssue ? TaskStatus.IN_PROGRESS : status],
    getStateConfig: (state) => {
      // On the workflow path the verify task completes at report time and the
      // rework loop spawns the next attempt — the own-completed gate keeps the
      // finished attempt from rendering the issue state.
      if (taskHasOpenIssue) {
        if (state.role === TaskParty.AGENT) {
          return {
            actions: (
              <Button
                size="sm"
                onClick={verificationDocModal.show}
                variant="secondary"
              >
                View issues
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Client fixing the reported issues"
                status={TaskStatus.IN_PROGRESS}
                timestamp={task.updatedAt}
              />
            ),
          };
        }
        if (state.role === TaskParty.CLIENT) {
          return {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Client fixing the reported issues"
                status={TaskStatus.IN_PROGRESS}
                timestamp={task.updatedAt}
              />
            ),
          };
        }
      }

      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={verificationDocModal.show}
                variant="primary"
              >
                Start
                <Icons.ArrowRight className="size-4" />
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Verify signed documents" />,
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                onClick={verificationDocModal.show}
                variant="secondary"
              >
                View documents
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                status={task.status}
                label="Verify signed documents"
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: <ServiceRequestTaskLabel label="Verify signed documents" />,
          },
        },
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda verifies and saves signed documents" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda verifies and saves signed documents" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                onClick={verificationDocModal.show}
                variant="secondary"
              >
                View documents
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                status={task.status}
                label="Commenda verifies and saves signed documents"
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <DocumentVerificationModal
        serviceRequest={serviceRequest}
        mutate={mutate}
        open={verificationDocModal.visible}
        onClose={verificationDocModal.hide}
        onMarkTaskAsComplete={async () => {
          await updateTaskStatus({
            task,
            mutate,
            status: TaskStatus.COMPLETED,
          });
          verificationDocModal.toggle();
        }}
        task={task}
        serviceRequestId={serviceRequest?.id}
      />
    </div>
  );
}
